import { useEffect, useState } from "react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { supabase } from "@/lib/supabase"
import { checkSubdomainAvailability, validateSubdomain } from "@/lib/subdomainRules"
import { useAuthStore } from "@/stores/authStore"
import { useOrgStore } from "@/stores/orgStore"

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function slugFromName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 30)
}

interface CreateChildOrgModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: () => void
}

export function CreateChildOrgModal({ open, onOpenChange, onCreated }: CreateChildOrgModalProps) {
  const { user } = useAuthStore()
  const { currentOrg, createChildOrg } = useOrgStore()
  const [name, setName] = useState("")
  const [slug, setSlug] = useState("")
  const [slugTouched, setSlugTouched] = useState(false)
  const [mode, setMode] = useState<"fiscal" | "affiliated">("fiscal")
  const [adminFeePct, setAdminFeePct] = useState("7.00")
  const [leaderEmail, setLeaderEmail] = useState("")
  const [loading, setLoading] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [inviteUrl, setInviteUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!slugTouched) setSlug(slugFromName(name))
  }, [name, slugTouched])

  const resetForm = () => {
    setName("")
    setSlug("")
    setSlugTouched(false)
    setMode("fiscal")
    setAdminFeePct("7.00")
    setLeaderEmail("")
    setFormError(null)
    setInviteUrl(null)
  }

  const handleClose = () => {
    resetForm()
    onOpenChange(false)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!currentOrg?.id || !user?.id) {
      toast.error("Organization not found")
      return
    }
    if (!name.trim()) {
      setFormError("Project name is required")
      return
    }
    const slugCheck = validateSubdomain(slug)
    if (!slugCheck.valid) {
      setFormError(slugCheck.error ?? "Invalid subdomain")
      return
    }
    const email = leaderEmail.trim().toLowerCase()
    if (email && !EMAIL_REGEX.test(email)) {
      setFormError("Please enter a valid project leader email")
      return
    }
    const fee = Number.parseFloat(adminFeePct)
    if (Number.isNaN(fee) || fee < 0 || fee > 100) {
      setFormError("Admin fee must be between 0 and 100")
      return
    }

    setLoading(true)
    setFormError(null)

    const available = await checkSubdomainAvailability(slug)
    if (!available) {
      setFormError("That subdomain is already taken")
      setLoading(false)
      return
    }

    const { data: org, error, seedWarning } = await createChildOrg({
      sponsorOrgId: currentOrg.id,
      name,
      slug,
      mode,
      adminFeePct: fee,
      // Without a leader invite the sponsorship starts active immediately.
      activate: email === "",
    })

    if (error || !org) {
      setFormError(error?.message ?? "Failed to create organization")
      setLoading(false)
      return
    }
    if (seedWarning) {
      toast.warning(
        "Organization created, but default pages could not be seeded. The project leader can pick a theme from their builder."
      )
    }

    let createdInviteUrl: string | null = null
    if (email) {
      const { data: invite, error: inviteError } = await supabase
        .from("invites")
        .insert({
          org_id: org.id,
          email,
          role: "admin",
          invited_by: user.id,
        })
        .select("token")
        .single()
      if (inviteError || !invite) {
        toast.warning(
          "Organization created, but the leader invite failed. You can re-invite from the project's team page."
        )
        console.error(inviteError)
      } else {
        createdInviteUrl = `${window.location.origin}/accept-invite?token=${invite.token}`
        setInviteUrl(createdInviteUrl)
        try {
          await navigator.clipboard.writeText(createdInviteUrl)
          toast.success(`Invite link copied! Share with ${email}`)
        } catch {
          toast.success("Invite created! Copy the link below.")
        }
      }
    }

    toast.success(`${org.name} created`)
    onCreated()
    setLoading(false)
    // Keep the dialog open only when there's an invite link to hand off.
    if (!createdInviteUrl) handleClose()
  }

  if (inviteUrl) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Project created</DialogTitle>
            <DialogDescription>
              Share this link with {leaderEmail} — accepting it makes them the project's admin
              and activates the sponsorship.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <Input readOnly value={inviteUrl} className="font-mono text-xs" />
            <Button
              type="button"
              variant="outline"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(inviteUrl)
                  toast.success("Link copied to clipboard")
                } catch {
                  toast.error("Failed to copy link")
                }
              }}
            >
              Copy
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">This invite link expires in 7 days.</p>
          <DialogFooter>
            <Button onClick={handleClose}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add organization</DialogTitle>
          <DialogDescription>
            Create a project under {currentOrg?.name}'s umbrella. The project gets its own site
            and dashboard; you keep portfolio visibility.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="child-name">Project name</Label>
              <Input
                id="child-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Riverside Youth Collective"
                disabled={loading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="child-slug">Subdomain</Label>
              <Input
                id="child-slug"
                value={slug}
                onChange={(e) => {
                  setSlugTouched(true)
                  setSlug(e.target.value.toLowerCase())
                }}
                placeholder="riverside-youth"
                disabled={loading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="child-mode">Relationship</Label>
              <Select
                value={mode}
                onValueChange={(v: "fiscal" | "affiliated") => setMode(v)}
                disabled={loading}
              >
                <SelectTrigger id="child-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fiscal">
                    <div className="flex flex-col">
                      <span className="font-medium">Fiscally sponsored</span>
                      <span className="text-xs text-muted-foreground">
                        No own EIN — donations route through your organization
                      </span>
                    </div>
                  </SelectItem>
                  <SelectItem value="affiliated">
                    <div className="flex flex-col">
                      <span className="font-medium">Affiliated</span>
                      <span className="text-xs text-muted-foreground">
                        Own 501(c)(3) — you get visibility, money stays with them
                      </span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            {mode === "fiscal" && (
              <div className="space-y-2">
                <Label htmlFor="child-fee">Admin fee %</Label>
                <Input
                  id="child-fee"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={adminFeePct}
                  onChange={(e) => setAdminFeePct(e.target.value)}
                  disabled={loading}
                />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="child-leader">Project leader email (optional)</Label>
              <Input
                id="child-leader"
                type="email"
                value={leaderEmail}
                onChange={(e) => setLeaderEmail(e.target.value)}
                placeholder="leader@example.org"
                disabled={loading}
              />
              <p className="text-xs text-muted-foreground">
                They'll be invited as the project's admin. Leave blank to invite someone later.
              </p>
            </div>
            {formError && <p className="text-sm text-destructive">{formError}</p>}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Creating..." : "Create project"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
